import React, { useEffect, useState } from 'react';

/**
 * Logout Handler
 *
 * Two modes:
 *
 * 1. User-initiated logout (no ?sid param):
 *    Clears session tokens and redirects to Entra logout to terminate the SSO session.
 *
 * 2. Front-channel logout — Entra calls this URL in a hidden iframe with ?sid=<sessionId>
 *    after the user logs out from any app in the SSO session. In this case we just clear
 *    local session storage and return a 200 page — no redirect, to avoid a loop.
 *
 * Access via: /manheimsupport/logout
 */
function Logout() {
  const [isFrontChannel, setIsFrontChannel] = useState(false);

  useEffect(() => {
    // Clear id_token from sessionStorage regardless of logout mode
    sessionStorage.removeItem('id_token');

    const params = new URLSearchParams(window.location.search);
    const sid = params.get('sid');

    if (sid) {
      // Front-channel logout notification from Entra (called in a hidden iframe).
      // Post the sid to the backend so it can mark the session as revoked.
      // The main window is polling /api/auth/session and will receive 401 → redirect to login.
      fetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sid })
      }).catch(() => {
        // Best-effort — if the backend call fails, the session will expire naturally
      });
      setIsFrontChannel(true);
      return;
    }

    // User-initiated logout: redirect to Entra to clear the SSO session
    const tenantId = process.env.REACT_APP_ENTRA_TENANT_ID;
    const postLogoutRedirectUri = encodeURIComponent(`${window.location.origin}/manheimsupport/login`);

    if (tenantId) {
      const logoutUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/logout?post_logout_redirect_uri=${postLogoutRedirectUri}`;
      window.location.href = logoutUrl;
    } else {
      window.location.href = '/manheimsupport/login';
    }
  }, []);

  if (isFrontChannel) {
    // Minimal response for Entra's front-channel logout iframe call
    return <div />;
  }

  return (
    <div className="auth-container">
      <div className="auth-spinner" />
    </div>
  );
}

export default Logout;
